import React from 'react';
import { SimulationInputs, AggregatedCostData, SimulationResults } from './types';
import styles from './simulazioneCosti.module.scss';
import { formatItalianPercentageNo100, formatItalianCurrency } from 'utils/formatUtils';

interface CostiTableProps {
  inputs: SimulationInputs;
  aggregatedData: AggregatedCostData;
  results: SimulationResults;
  onInputChange: (field: string, value: any) => void;
}

interface TableRowProps {
  label: string;
  euroValue: number | string;
  percentageValue?: number | string;
  percentageInput?: React.ReactNode;
  isBold?: boolean;
  disabled?: boolean;
  tooltip?: string;
}

const TableRow: React.FC<TableRowProps> = ({
  label,
  euroValue,
  percentageValue,
  percentageInput,
  isBold = false,
  disabled = false,
  tooltip,
}) => {
  const rowClassName = `${styles.tableRow} ${isBold ? styles.boldRow : ''} ${disabled ? styles.disabledRow : ''}`;

  return (
    <tr className={rowClassName}>
      <td>
        <span className={styles.rowLabel}>
          {label}
          {tooltip && (
            <span className={styles.tooltip}>
              <span className={styles.tooltipIcon}> (?)</span>
              <span className={styles.tooltipContent}>{tooltip}</span>
            </span>
          )}
        </span>
      </td>
      <td>{typeof euroValue === 'number' ? formatItalianCurrency(euroValue) : euroValue}</td>
      <td>
        {percentageInput ||
          (typeof percentageValue === 'number'
            ? formatItalianPercentageNo100(percentageValue)
            : percentageValue || '-')}
      </td>
    </tr>
  );
};

interface DropdownInputProps {
  value: number;
  options: { value: number; label: string }[];
  onChange: (value: number) => void;
  disabled?: boolean;
}

const DropdownInput: React.FC<DropdownInputProps> = ({ value, options, onChange, disabled = false }) => {
  return (
    <select
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      className={styles.dropdownInput}
      disabled={disabled}
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
};

// Generate percentage options for dropdown
const generatePercentageOptions = (min: number, max: number, step: number) => {
  const options: { value: number; label: string }[] = [];
  for (let value = min; value <= max + 0.001; value += step) {
    const roundedValue = Math.round(value * 100) / 100;
    options.push({
      value: roundedValue,
      label: formatItalianPercentageNo100(roundedValue),
    });
  }
  return options;
};

// Pre-generate options for Fee Only (0-2.20%) and Fee on Top (0-1.00%)
const feeOnlyOptions = generatePercentageOptions(0, 2.2, 0.05);
const feeOnTopOptions = generatePercentageOptions(0, 1.0, 0.05);

export const CostiTable: React.FC<CostiTableProps> = ({ inputs, aggregatedData, results, onInputChange }) => {
  const isFeeOnly = inputs.feeRegime === 'fee_only';
  const isFeeOnTop = inputs.feeRegime === 'fee_on_top';

  // Get Fee on Top inputs for differenziata logic
  const feeOnTopInputs = inputs.feeOnTop;

  // Check if using Unica or Differenziata mode (Fee on Top only)
  const isUnica =
    isFeeOnTop &&
    (feeOnTopInputs.commissioneConsulenza > 0 ||
      ((feeOnTopInputs.feeFondi ?? 0) === 0 &&
        (feeOnTopInputs.feeETF ?? 0) === 0 &&
        (feeOnTopInputs.feeAmministrato ?? 0) === 0));

  const isDifferenziata =
    isFeeOnTop &&
    ((feeOnTopInputs.feeFondi ?? 0) > 0 ||
      (feeOnTopInputs.feeETF ?? 0) > 0 ||
      (feeOnTopInputs.feeAmministrato ?? 0) > 0);

  const commissioneOptions = isFeeOnly ? feeOnlyOptions : feeOnTopOptions;
  const fieldPrefix = isFeeOnly ? 'feeOnly' : 'feeOnTop';

  return (
    <div className={styles.tableContainer}>
      <table className={styles.costiTable}>
        <thead className={styles.tableHeader}>
          <tr>
            <th rowSpan={2} className={styles.headerFirstColumn}>
              Tipo commissione
            </th>
            <th colSpan={2} className={styles.headerSimulazione}>
              Simulazione
            </th>
          </tr>
          <tr>
            <th className={styles.headerSecondRow}>Controvale annuo (€)</th>
            <th className={styles.headerSecondRow}>% su patrimonio</th>
          </tr>
        </thead>
        <tbody>
          {/* Commissione di consulenza */}
          <TableRow
            label="Commissione di consulenza"
            euroValue={results.commissioneConsulenzaEuro}
            percentageInput={
              <DropdownInput
                value={isFeeOnly ? inputs.feeOnly.commissioneConsulenza : inputs.feeOnTop.commissioneConsulenza}
                options={commissioneOptions}
                onChange={(v) => onInputChange(`${fieldPrefix}.commissioneConsulenza`, v)}
                disabled={isFeeOnTop && isDifferenziata}
              />
            }
            disabled={isFeeOnTop && isDifferenziata}
          />

          {/* Fee on Top - Differenziata fields */}
          {isFeeOnTop && (
            <>
              <TableRow
                label="Fee fondi"
                euroValue={results.feeFondiEuro || '-'}
                percentageInput={
                  <DropdownInput
                    value={feeOnTopInputs.feeFondi ?? 0}
                    options={feeOnTopOptions}
                    onChange={(v) => onInputChange('feeOnTop.feeFondi', v)}
                    disabled={isUnica && feeOnTopInputs.commissioneConsulenza > 0}
                  />
                }
                disabled={isUnica && feeOnTopInputs.commissioneConsulenza > 0}
              />
              <TableRow
                label="Fee ETF"
                euroValue={results.feeETFEuro || '-'}
                percentageInput={
                  <DropdownInput
                    value={feeOnTopInputs.feeETF ?? 0}
                    options={feeOnTopOptions}
                    onChange={(v) => onInputChange('feeOnTop.feeETF', v)}
                    disabled={isUnica && feeOnTopInputs.commissioneConsulenza > 0}
                  />
                }
                disabled={isUnica && feeOnTopInputs.commissioneConsulenza > 0}
              />
              <TableRow
                label="Fee Amministrato"
                euroValue={results.feeAmministratoEuro || '-'}
                percentageInput={
                  <DropdownInput
                    value={feeOnTopInputs.feeAmministrato ?? 0}
                    options={feeOnTopOptions}
                    onChange={(v) => onInputChange('feeOnTop.feeAmministrato', v)}
                    disabled={isUnica && feeOnTopInputs.commissioneConsulenza > 0}
                  />
                }
                disabled={isUnica && feeOnTopInputs.commissioneConsulenza > 0}
              />
            </>
          )}

          {/* Servizi Aggiuntivi */}
          <TableRow
            label="Servizi Aggiuntivi"
            euroValue={results.serviziAggiuntiviEuro}
            percentageInput={
              <DropdownInput
                value={isFeeOnly ? inputs.feeOnly.serviziAggiuntivi : inputs.feeOnTop.serviziAggiuntivi}
                options={[
                  { value: 0, label: formatItalianPercentageNo100(0) },
                  { value: 0.2, label: formatItalianPercentageNo100(0.2) },
                ]}
                onChange={(v) => onInputChange(`${fieldPrefix}.serviziAggiuntivi`, v)}
              />
            }
            tooltip="I Servizi aggiuntivi prevedono l'invio del report di monitoraggio a domicilio e la possibilità di ricevere degli alert sulla performance di portafoglio"
          />

          {/* Abbuoni (Fee Only only) */}
          {isFeeOnly && (
            <TableRow
              label="Abbuoni"
              euroValue={results.abbuoniEuro}
              percentageValue={aggregatedData.abbuoniPercentage}
            />
          )}

          {/* Data-based rows */}
          <TableRow
            label="Commissioni di gestione"
            euroValue={results.commissioniGestioneEuro}
            percentageValue={aggregatedData.commissioniGestione}
          />
          <TableRow
            label="Altre commissioni ricorrenti"
            euroValue={results.altreCommissioniRicorrentiEuro}
            percentageValue={aggregatedData.altreCommissioniRicorrenti}
          />
          <TableRow
            label="Commissioni di transazione"
            euroValue={results.commissioniTransazioneEuro}
            percentageValue={aggregatedData.commissioniTransazione}
          />
          <TableRow
            label="Commissioni di performance"
            euroValue={results.commissioniPerformanceEuro}
            percentageValue={aggregatedData.commissioniPerformance}
          />

          {/* Includi fondi FAM (Fee on Top only) */}
          {isFeeOnTop && (
            <tr className={styles.tableRow}>
              <td>Includi fondi FAM</td>
              <td>-</td>
              <td>
                <select
                  value={feeOnTopInputs.includiFondiFAM ? 'SI' : 'NO'}
                  onChange={(e) => onInputChange('feeOnTop.includiFondiFAM', e.target.value === 'SI')}
                  className={styles.famToggle}
                >
                  <option value="SI">SI</option>
                  <option value="NO">NO</option>
                </select>
              </td>
            </tr>
          )}

          {/* Summary rows */}
          <TableRow
            label="Totale costi"
            euroValue={results.totaleCostiEuro}
            percentageValue={results.totaleCostiPercentage}
            isBold
          />
          <TableRow
            label="di cui addebiti netti sul CC"
            euroValue={results.addebitiNettiCCEuro}
            percentageValue={results.addebitiNettiCCPercentage}
            isBold
          />
          <TableRow
            label="Stima rebates pfa"
            euroValue={results.stimaRebatesPfaEuro}
            percentageValue={results.stimaRebatesPfaPercentage}
          />
        </tbody>
      </table>
    </div>
  );
};
